import { gql, useLazyQuery } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import { Basket_Response } from 'shared/presenters/graphqlTypes'
import useBasket from './useBasket'
import { useEnthusiast } from 'shared/state/useAuth'
import { definedNotNull } from 'shared/presenters/presenter'
import { useEffect } from 'react'

/**
 * Return the basket total price via API call to use the backend as the source of truth.
 */
const useBasketTotalPrice = () => {
  const { enthusiast } = useEnthusiast()
  const { basket, basketLoading, refetchBasket } = useBasket()

  const lineItems = basket?.line_items

  const basketTotalPricePreAdjustments = lineItems?.reduce(
    (prev, cur) => prev + definedNotNull(cur?.price_paid),
    0
  )

  /**
   * basketForm corresponds to the naming of the basket form action on the backend
   * we fetch the 'basketForm' upon basket retrieval currently until changes are
   * made to support get the basket total price back by other means.
   * see https://github.com/eola/eola/issues/2276 for further discussion
   */
  const [
    getBasketForm,
    {
      loading: basketTotalPriceLoading,
      data: basketTotalPrice,
      error: basketTotalPriceError
    }
  ] = useLazyQuery<{ Consumer_Baskets_GetBasketForm: Basket_Response }>(
    TOTAL_PRICE,
    {
      fetchPolicy: 'no-cache',
      onError({ message }) {
        /**
         * Incomplete Activity Voucher booking returns this error,
         * therefore can be ignored, will be replaced by invalid
         * state handling system
         */
        if (message.includes('Booking needs a time slot')) {
          return
        } else {
          // For all other errors we reset basket
          refetchBasket({ basketId: null })
        }
      }
    }
  )

  const basketTotalPriceLoaded = typeof basketTotalPrice != 'undefined'

  useEffect(() => {
    if (!basket?.id || basketLoading) {
      return
    }

    try {
      getBasketForm({
        variables: {
          basketId: basket.id,
          enthusiastName: enthusiast?.name,
          enthusiastEmail: enthusiast?.email
        }
      })
    } catch (err) {
      Sentry.captureException(err)
    }
    // TODO #6362 - Fix ESLint - hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket, basketLoading, enthusiast])

  return {
    basketTotalPriceLoading,
    basketTotalPriceLoaded,
    basketTotalPrice:
      basketTotalPrice?.Consumer_Baskets_GetBasketForm
        ?.price_after_adjustments_pence ?? 0,
    enthusiastFeePence:
      basketTotalPrice?.Consumer_Baskets_GetBasketForm?.enthusiast_fee_pence ??
      0,
    basketTotalPricePreAdjustments,
    basketTotalPriceError
  }
}

export default useBasketTotalPrice

const TOTAL_PRICE = gql`
  query WidgetUseBasketTotalPrice(
    $basketId: ID
    $enthusiastName: String
    $enthusiastEmail: String
  ) {
    Consumer_Baskets_GetBasketForm(
      basket_id: $basketId
      enthusiast_name: $enthusiastName
      enthusiast_email: $enthusiastEmail
    ) {
      price_after_adjustments_pence
      enthusiast_fee_pence
    }
  }
`
